import { resources } from "@constants/hardcoded-strings";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { ServiceTemplateDetailsComponentState } from "@containers/management-console/service-template-details/service-template-details-component/service-template-details-component";
import { UpdateTemplateJSONModal } from "@containers/modals/update-template-json-modal/update-template-json-modal";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ModalTypes } from "@enums/modal-types";
import { SelectOption } from "@idw-types/common";
import { MetadataServiceTemplateValidationErrors } from "@idw-types/common/validation/service-template/service-template-validation-errors";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { FormattedServiceTemplateDetails } from "@idw-types/data/service-template-details/service-template-details";
import { ChangeEvent } from "react";
import Button from "react-bootstrap/Button";
import styles from "./service-template-metadata-container.less";

type ServiceTemplateMetadataContainerProps = {
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    serviceTemplateDetailsState: ServiceTemplateDetailsComponentState;
    validationErrors: MetadataServiceTemplateValidationErrors;
    axnCompanies: Array<AxnCompany>;
    setServiceTemplateDetailsState: (serviceTemplateDetailsState: ServiceTemplateDetailsComponentState) => void;
}

export const ServiceTemplateMetadataContainer = (props: ServiceTemplateMetadataContainerProps) => {
    const resource = resources.pages.serviceTemplateDetails.tabs.metadata;
    const modalContext = useModalContext();
    const { isDisabled, serviceTemplateDetailsState, labelWidth, axnCompanies, validationErrors, setServiceTemplateDetailsState } = props;
    const { serviceTemplate, selectedOrganizationOption } = serviceTemplateDetailsState;
    
    const setServiceTemplate = (serviceTemplate: FormattedServiceTemplateDetails) => {
        setServiceTemplateDetailsState({
            ...serviceTemplateDetailsState,
            serviceTemplate
        });
    }     

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setServiceTemplate({
            ...serviceTemplate,
            name: event.target.value
        });
    };

    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        setServiceTemplate({
            ...serviceTemplate,
            description: event.target.value
        });
    };

    const updateTemplateString = (newTemplateString: string) => {
        setServiceTemplate({
            ...serviceTemplate,
                ssoTemplate: newTemplateString
        });
    };


    const onUpdateTemplateJSONClick = () => {
        const renderUpdateTemplateJSONModal = (onModalClose: () => void) => {
            return (
                <UpdateTemplateJSONModal
                    onModalClose={onModalClose}
                    templateString={serviceTemplate.ssoTemplate}
                    updateTemplateString={updateTemplateString}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.UpdateTemplateJSON,
            renderFunction: renderUpdateTemplateJSONModal
        });
    };

    const handleOrganizationChange = (newValue: SelectOption<string>) => {
        setServiceTemplateDetailsState({
                ...serviceTemplateDetailsState,
                serviceTemplate: {
                    ...serviceTemplate,
                    organization: axnCompanies.find(company => company.id === newValue?.value) || null
                },  
                selectedOrganizationOption: newValue
            });
        };

    const handleStatusChange = () => {
        const newStatus = serviceTemplate.status === EntityStatusTypes.ACTIVE
            ? EntityStatusTypes.INACTIVE
            : EntityStatusTypes.ACTIVE;

        setServiceTemplate({
            ...serviceTemplate,
            status: newStatus
        });
    };

    return (
        <div className={styles.serviceTemplateMetadataContainer}>
            <InputFormGroup
                controlId="service-template-name-input"
                automationId="service-template-name-input"
                label={resource.inputs.name.label}
                ariaLabel={resource.inputs.name.label}
                value={serviceTemplate.name || ""}
                disabled={isDisabled}
                isRequired={true}
                onChange={handleNameChange}
                labelWidth={labelWidth}
            />
            {validationErrors.nameError}

            <InputFormGroup
                controlId="service-template-description-input"
                automationId="service-template-description-input"
                label={resource.inputs.description.label}
                ariaLabel={resource.inputs.description.label}
                value={serviceTemplate.description || ""}
                disabled={isDisabled}
                isRequired={true}
                onChange={handleDescriptionChange}
                labelWidth={labelWidth}
            />
            {validationErrors.descriptionError}

            <OrganizationSelectFormGroup
                idPrefix="service-template"
                onChange={handleOrganizationChange}
                axnCompanies={axnCompanies}
                selectedOption={selectedOrganizationOption}
                disabled={isDisabled}
                isRequired={false}
                isSearchable={true}
                isClearable={true}
                labelWidth={labelWidth}
            />

            <TextareaFormGroup
                controlId="service-template-template-json-input"
                automationId="service-template-template-json-input"
                label={resource.inputs.templateJSON.label}
                ariaLabel={resource.inputs.templateJSON.label}
                value={serviceTemplate.ssoTemplate || ""}
                disabled={true}
                isRequired={true}
                onChange={() => { }}
                labelWidth={labelWidth}
            />
            {validationErrors.ssoTemplateError}

            <div className={styles.buttonDiv}>
                <Button id="service-template-update-template-json-button" disabled={isDisabled} onClick={onUpdateTemplateJSONClick}>
                    {resource.buttons.updateTemplateJSON.label}
                </Button>
            </div>

            <ToggleFormGroup
                controlId={"service-template-status-toggle"}
                automationId={"service-template-status-toggle"}
                label={resources.common.labels.status}
                ariaLabel={resources.common.labels.status}
                checkedIcon={false}
                uncheckedIcon={false}
                labelWidth={labelWidth}
                checked={serviceTemplate.status === EntityStatusTypes.ACTIVE}
                disabled={isDisabled}
                isRequired={true}
                onChange={handleStatusChange}
                value={`${serviceTemplate.status}`}
            />

        </div>
    );
}